import { useCallback, useEffect, useRef, useState } from "react";
import type { Photo } from "@/types";
import { MaterialIcon } from "@/components/MaterialIcon";
import styles from "./PlayerImageDisplay.module.css";

interface PlayerImageDisplayProps {
  photo: Photo | undefined;
  velocity: number;
  isImageCached: (url: string) => boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  showPlayPauseOverlay?: boolean;
}

/**
 * Player image display component with single-buffer approach
 *
 * Features:
 * - Single image element (no double-buffering complexity)
 * - Only updates src when image is cached (no flicker)
 * - Quality: thumb400 for playback (fallback thumb200), thumb1920 when stopped
 * - Background preloading for non-cached images
 */
export function PlayerImageDisplay({
  photo,
  velocity,
  isImageCached,
  onClick,
  onDoubleClick,
  showPlayPauseOverlay = false,
}: PlayerImageDisplayProps) {
  // Single image URL - only update when new image is ready
  const [displayUrl, setDisplayUrl] = useState(() => photo?.urls?.thumb400 || photo?.urls?.thumb200 || "");

  // Play/pause overlay icon state
  const [overlayIcon, setOverlayIcon] = useState<'play' | 'pause' | null>(null);
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevVelocityRef = useRef(velocity);

  // Zoom/pan state
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for tracking
  const lastPhotoIdRef = useRef<string>(photo?.photoId || "");
  const upgradeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preloadImgRef = useRef<HTMLImageElement | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Clamp a value between min and max
   */
  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

  /**
   * Handle mouse down for pan start
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only allow panning when zoomed in
      if (scale <= 1) return;

      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
    },
    [scale, pan]
  );

  /**
   * Handle mouse move for panning
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !containerRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const rect = containerRef.current.getBoundingClientRect();
      const maxPanX = (rect.width * (scale - 1)) / 2;
      const maxPanY = (rect.height * (scale - 1)) / 2;

      setPan({
        x: clamp(dragStartRef.current.panX + deltaX, -maxPanX, maxPanX),
        y: clamp(dragStartRef.current.panY + deltaY, -maxPanY, maxPanY),
      });
    },
    [isDragging, scale]
  );

  /**
   * Handle mouse up for pan end
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Handle photo change - simple single-buffer approach
   * Only update display when image is cached OR preload completes
   */
  useEffect(() => {
    if (!photo || photo.photoId === lastPhotoIdRef.current) return;

    lastPhotoIdRef.current = photo.photoId;
    // Prefer thumb400 for better playback quality, fallback to thumb200
    const newUrl = photo.urls.thumb400 || photo.urls.thumb200;

    // Reset zoom/pan on photo change
    setScale(1);
    setPan({ x: 0, y: 0 });

    if (!newUrl) return;

    // Cancel any pending quality upgrade
    if (upgradeTimerRef.current) {
      clearTimeout(upgradeTimerRef.current);
      upgradeTimerRef.current = null;
    }

    // Cancel any pending preload
    if (preloadImgRef.current) {
      preloadImgRef.current.onload = null;
      preloadImgRef.current.onerror = null;
    }

    // If cached, update immediately
    if (isImageCached(newUrl)) {
      setDisplayUrl(newUrl);
      return;
    }

    // Not cached - preload in background, update when ready
    const loadingPhotoId = photo.photoId;
    const img = new Image();
    preloadImgRef.current = img;

    img.onload = () => {
      // Only update if this is still the current photo
      if (lastPhotoIdRef.current === loadingPhotoId) {
        setDisplayUrl(newUrl);
      }
    };
    img.onerror = null; // Ignore errors
    img.src = newUrl;
  }, [photo, isImageCached]);

  /**
   * Quality upgrade when stopped:
   * - Only upgrade to high quality when completely stopped (velocity = 0)
   * - Uses thumb1920 for sharp still frames
   * - During playback, use thumb400 for good quality without flickering
   */
  useEffect(() => {
    if (!photo) return;

    // Cancel any existing timer
    if (upgradeTimerRef.current) {
      clearTimeout(upgradeTimerRef.current);
      upgradeTimerRef.current = null;
    }

    const absVelocity = Math.abs(velocity);

    // Only upgrade when completely stopped
    if (absVelocity < 1) {
      const targetUrl = photo.urls.thumb1920;

      if (!targetUrl) return;

      // Small delay to ensure user has stopped
      upgradeTimerRef.current = setTimeout(() => {
        const img = new Image();
        img.onload = () => {
          // Update display directly
          setDisplayUrl(targetUrl);
        };
        img.src = targetUrl;
      }, 200);
    }

    return () => {
      if (upgradeTimerRef.current) {
        clearTimeout(upgradeTimerRef.current);
      }
    };
  }, [velocity, photo]);

  // Handle missing photo
  if (!photo) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>No photo selected</div>
      </div>
    );
  }

  // Show play/pause overlay icon on velocity change (Netflix-style)
  useEffect(() => {
    if (!showPlayPauseOverlay) return;

    const wasPlaying = Math.abs(prevVelocityRef.current) > 0.1;
    const isPlaying = Math.abs(velocity) > 0.1;
    prevVelocityRef.current = velocity;

    // Only show icon on play/pause transitions
    if (wasPlaying === isPlaying) return;

    // Clear previous timer
    if (overlayTimerRef.current) {
      clearTimeout(overlayTimerRef.current);
    }

    setOverlayIcon(isPlaying ? 'play' : 'pause');

    // Fade out after 600ms
    overlayTimerRef.current = setTimeout(() => {
      setOverlayIcon(null);
      overlayTimerRef.current = null;
    }, 600);

    return () => {
      if (overlayTimerRef.current) {
        clearTimeout(overlayTimerRef.current);
      }
    };
  }, [velocity, showPlayPauseOverlay]);

  // Single click vs double click disambiguation
  // Delays single click by 250ms to check if a double click follows
  const handleClick = useCallback(() => {
    if (scale > 1) return; // No click actions when zoomed

    if (clickTimerRef.current) {
      // Second click arrived — it's a double click, cancel single click
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      onDoubleClick?.();
    } else {
      // First click — wait to see if a second click follows
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        onClick?.();
      }, 250);
    }
  }, [scale, onClick, onDoubleClick]);

  // Cleanup click timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  // Determine cursor style based on zoom state
  const getCursorClass = () => {
    if (scale > 1) {
      return isDragging ? styles.containerDragging : styles.containerZoomed;
    }
    return '';
  };

  // Transform style for zoom/pan
  const imageTransform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${getCursorClass()}`}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Single image - updated only when cached or preloaded */}
      <img
        src={displayUrl}
        alt=""
        className={styles.image}
        style={{
          transform: imageTransform,
          transformOrigin: 'center center',
        }}
        draggable={false}
      />

      {/* Netflix-style play/pause overlay icon */}
      {overlayIcon && (
        <div className={styles.playPauseOverlay}>
          <MaterialIcon
            name={overlayIcon === 'play' ? 'play_circle' : 'pause_circle'}
            size={80}
            filled
            style={{ color: 'rgba(255, 255, 255, 0.85)' }}
          />
        </div>
      )}
    </div>
  );
}
